import React, {Fragment, useState} from 'react';
import createEditor, {referenceActionData} from '../form/ComponentEditor';
import Box from '../layout/Box';
import BoxEditor from "./BoxEditor";
import PromotionList from "./lists/PromotionList";
import PromotionForm from "./forms/PromotionForm";
import DiscountEditor from "./DiscountEditor";
import ModalEditor from "./ModalEditor";
import Button from "../form/Button";
import SelectorGrid from "../form/SelectorGrid/SelectorGrid";
import ComponentConfig from "../form/ComponentConfig";
import {CurrencyFormatter, ProductColumn} from "../../utils/formatters";
import Select from "../form/Select";
import TextInput from "../form/TextInput";
import {createComponent, fieldFilter, loadComponent} from "../../utils/components";
import ComponentLoader from "../form/ComponentLoader";
import debounce from "lodash-es/debounce";
import {connect} from "src/store";
import PointOfSaleSelectorGrid from "../form/PointOfSale/PointOfSaleSelectorGrid";
import {setContent} from "../../actions";

const PromotionRetailProduct = (props) => {
    const [sort, setSort] = useState("created_at.desc");
    let columns = {
        "displayname": {
            header: 'Megnevezés',
            ...ProductColumn(props.component === "product" ? "id" : "product_id"),
            width: 2,
            filterable: true
        },
        "listprice": {
            header: 'Lista Ár',
            width: 1,
            filterable: true,
            renderer: CurrencyFormatter,
            className: "currency"
        },
        "saleprice": {
            header: 'Eladási Ár',
            width: 1,
            filterable: true,
            renderer: CurrencyFormatter,
            className: "currency"
        },
        "priority": {
            header: 'Sorrend',
            width: 1,
            operator: "=",
            renderer: !props.disabled ? (value, rowData, rowIndex, columnprops, parent) => {
                const saveChanges = debounce(async (field, value) => {
                    await createComponent("priority", {
                        data: {
                            list_component_type: "Promotion",
                            list_component_id: props.values[props.componentConfig.idfield],
                            option_type: "PromotionRetailProduct",
                            option_id: rowData.product_id,
                            priority: value
                        }
                    });
                    parent.refresh();
                }, 300);
                return <TextInput
                    label={false}
                    type="number"
                    placeholder={"Sorrend... Pl.: " + (rowIndex+1) + "."}
                    name="priority"
                    value={value}
                    saveChanges={saveChanges}
                />
            } : null,
            filterable: true,
        }
    }
    if (props.component === "ShopProduct") {
        columns = {
            "displayname": {
                header: 'Megnevezés',
                ...ProductColumn(props.component === "product" ? "id" : "product_id"),
                width: 2,
                filterable: true
            },
            "barcode": {
                header: 'Vonalkód',
                width: 1,
                filterable: true,
            },
            "sku": {
                header: 'ISBN',
                width: 1,
                filterable: true,
            },
            "listprice": {
                header: 'Lista Ár',
                width: 1,
                filterable: true,
                renderer: CurrencyFormatter,
                className: "currency"
            },
            "lowest_price": {
                header: 'Az elmúlt hónap legalacsonyabb ára',
                width: 1,
                renderer: CurrencyFormatter,
                className: "currency"
            },
            "saleprice": {
                header: 'Eladási Ár',
                width: 1,
                renderer: CurrencyFormatter,
                className: "currency"
            },
        };
    }
    return <Box
        theme="default"
        header={props.header || "Az akcióban résztvevő termékek"}
        actions={!props.component ? <Select
            label={false}
            storeType="local"
            options={[
                {id: "created_at.desc", label: "Újdonság"},
                {id: "listprice.asc", label: "Ár szerint növekvő"},
                {id: "listprice.desc", label: "Ár szerint csökkenő"},
                {id: "displayname.asc", label: "A-Z"},
                {id: "displayname.desc", label: "Z-A"},
            ]}
            value={sort}
            objectValue={false}
            valueKey="id"
            labelKey="label"
            saveChanges={(field, value) => {
                setSort(value);
            }}
        /> : null}
    >
        <SelectorGrid
            option="PromotionRetailProducts"
            component={props.component || "RetailProduct"}
            view={["id", "product_id"]}
            exports={[
                {
                    as: "excel",
                    params: props.component === "ShopProduct" ? {name: props.values.name} : undefined,
                    label: "Excel",
                    icon: "far fa-file-excel",
                    view: !props.component ? [
                        "displayname",
                        "listprice",
                        "saleprice",
                    ] : (props.component === "ShopProduct" ? [
                        "displayname",
                        "barcode",
                        "sku",
                        "listprice",
                        "lowest_price",
                        "saleprice",
                    ] : [
                        "displayname",
                        "listprice",
                    ]),
                }
            ]}
            onLoadCompleted={async (response) => {
                if (response && response.rows.length && !props.disabled) {
                    const list = await loadComponent("componentlist", {
                        component: "priority",
                        itemsperpage: response.fullcount,
                        view: ["priority", "option_id"],
                        filter: [
                            fieldFilter("list_component_type", "Promotion"),
                            fieldFilter("list_component_id", props.values[props.componentConfig.idfield]),
                            fieldFilter("option_type", "PromotionRetailProduct"),
                            fieldFilter("option_id", response.rows.map((row) => {
                                return row.product_id
                            }), "in"),
                        ]
                    });
                    list.items.map((row) => {
                        response.rows = response.rows.map((item) => {
                            if (item.product_id + "" === "" + row.option_id) {
                                item.priority = row.priority;
                            }
                            return item;
                        });
                    });
                }
                return response;
            }}
            columns={columns}
            reqparams={{
                promotionfilter: {
                    field: "id",
                    value: props.values[props.componentConfig.idfield]
                }
            }}
            data={props.values}
            orderBy={{field: sort.split(".")[0], direction: sort.split(".")[1].toUpperCase()}}
            {...props.getFieldProps("retailproducts")}
        />
    </Box>;
}

export class PromotionEditor extends React.Component {

    loadForm = async (data) => {
        await this.props.loadForm(data);
        this.editor && this.editor.focus();
    }

    pristineForm = async () => {
        await this.props.pristineForm();
        this.editor && this.editor.focus();
    }

    saveForm = async (event) => {
        await this.props.saveForm(event);
        this.list && this.list.refresh();
    }

    render() {
        const List = this.props.list || Box;
        const Editor = this.props.editor || BoxEditor;
        return (
            <Fragment>
                <List
                    {...this.props}
                    theme="default"
                    header={this.props.listHeader || "Promóciók"}
                    footer={[
                        <Button key="0" type="button" color="default" disabled={this.props.isLoading} onClick={ this.pristineForm }>
                            Új
                        </Button>
                    ]}
                >
                    <PromotionList ref={(el) => this.list = el} {...this.props} loadForm={this.loadForm} />
                </List>
                <Editor
                    {...this.props}
                    ref={(editor) => this.editor = editor}
                    theme="default"
                    header={this.props.editorHeader || "Promóció"}
                    footer={[
                        <Button key="0"
                                type="button"
                                disabled={!this.props.dirty || this.props.isLoading}
                                color="primary"
                                className="pull-right"
                                onClick={this.saveForm}
                        >
                            Mentés
                        </Button>,
                        <Button
                            key="1"
                            disabled={!this.props.values[this.props.componentConfig.idfield] || this.props.isLoading}
                            color="danger"
                            className="pull-left"
                            onClick={this.props.deleteForm}
                        >
                            Törlés
                        </Button>
                    ]}
                >
                    <ComponentConfig componentName="Promotion" componentConfigAs="promotionConfig">
                        <PromotionForm {...this.props} />
                    </ComponentConfig>
                </Editor>
                {
                    this.props.values[this.props.componentConfig.idfield]
                    &&
                    <DiscountEditor
                        gridid={"promotionEditorDiscountList"}
                        presets={{promotion: this.props.values}}
                        isLoading={this.props.isLoading}
                        editor={ModalEditor}
                        selection={null}
                        listFilter={[{
                            field: "promotion",
                            value: this.props.values[this.props.componentConfig.idfield]
                        }]}
                    />
                }
                {
                    this.props.values[this.props.componentConfig.idfield]
                    &&
                    <ComponentConfig componentName="Promotion">
                        <PointOfSaleSelectorGrid
                            name="pos"
                            data={this.props.values}
                            {...this.props.getFieldProps("pos")}
                        />
                    </ComponentConfig>
                }
                {
                    this.props.values[this.props.componentConfig.idfield]
                    &&
                    <PromotionRetailProduct {...this.props} />
                }
            </Fragment>
        );
    }
}

const ShopPromotions = (props) => {
    return props.values.items.map((promotion, idx) => {
        return <ComponentConfig key={idx} componentName="Promotion">
            <PromotionRetailProduct
                component="ShopProduct"
                header={promotion.name}
                values={promotion}
                getFieldProps={() => ({})}
                disabled={true}
            />
        </ComponentConfig>
    })
}

function removeDuplicates(myArr, prop) {
    return myArr.filter((obj, pos, arr) => {
        return arr.map(mapObj => mapObj[prop]).indexOf(obj[prop]) === pos;
    });
}

const ShopDiscounts = (props) => {
    const promotions = removeDuplicates(props.values.items
        .filter(({conditions}) => conditions.length === 0)
        .map(({promotion}) => promotion), "id");
    return <ShopPromotions {...props} values={{items: promotions}} />;
}

export const ShopPromotionsEditor = connect((state) => {
    return {
        till: state.app.till,
        pos: state.app.till.pos
    };
}, {setContent})((props) => {
    if (
        !(props.till && props.till.pos && props.till.pos.location && props.till.pos.location.store && props.till.pos.location.store.id + "" === "" + props.till.pos.location.store.store)
    ) {
        return null;
    }
    return <ComponentLoader
        componentName="componentlist"
        component="Discount"
        view={["promotion","conditions"]}
        filter={[
            {field: "type", value: "itemdiscount"},
            {field: "promotion.active", value: 1},
            {field: "promotion.pos", value: props.pos.id},
            {
                filters: [
                    {field: "promo_code", operator: "isnull"},
                    {field: "promo_code", value: ""},
                ],
                operator: "OR"
            },
        ]}
        itemsperpage={0}
    >
        <ShopDiscounts {...props} />
    </ComponentLoader>
});

export default createEditor({
    componentName: "Promotion",
    WrappedComponent: PromotionEditor,
    props: {
        view: "admin",
        type: "promotion",
        listFilter: [{
            field: "type", value: "promotion"
        }],
        componentActions: async (action, data, params, componentConfig) => {
            data = await referenceActionData(data, componentConfig);
            data.type = "promotion";
            return {action,data,params};
        }
    }
});
